import { useEffect, useState } from "react";
import { CalendarClock, AlertTriangle } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { api } from "@/lib/api";
import { formatDateEU } from "@/lib/constants";

const SOURCE_COLORS = {
  lead: "#457B9D",
  sample: "#7B6DCC",
};

const dayDiff = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const t = new Date();
  d.setHours(0,0,0,0); t.setHours(0,0,0,0);
  return Math.round((d - t) / (1000 * 60 * 60 * 24));
};

export default function FollowUps() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.listFollowUps().then(setItems);
  }, []);

  return (
    <div data-testid="page-followups">
      <PageHeader
        overline="Action Queue"
        title="Follow Ups"
        subtitle="Leads and samples awaiting a next step. Closest dates first."
      />

      {items.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title="No follow ups scheduled"
          description="Set a 'Next Follow Up Date' on any lead or 'Follow Up Date' on a sample to see it here."
        />
      ) : (
        <div className="card-soft overflow-hidden">
          <ul className="divide-y divide-[#E5E5E5]">
            {items.map((f) => {
              const diff = dayDiff(f.follow_up_date);
              const overdue = diff !== null && diff < 0;
              const today = diff === 0;
              return (
                <li
                  key={f.id}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-[#F7F1E8]/40"
                  data-testid={`followup-${f.id}`}
                >
                  <div
                    className="w-1.5 self-stretch rounded-full shrink-0"
                    style={{ backgroundColor: SOURCE_COLORS[f.source] || "#E5E5E5" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-[#222] truncate">{f.company}</h3>
                      <span
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold"
                        style={{
                          backgroundColor: `${SOURCE_COLORS[f.source]}1f`,
                          color: SOURCE_COLORS[f.source],
                        }}
                      >
                        {f.source}
                      </span>
                    </div>
                    <div className="text-xs text-[#6C757D] mt-0.5">
                      {f.reason} · {f.contact_person || "—"} · {f.status}
                    </div>
                    {f.notes && <div className="text-sm text-[#222]/80 mt-1 line-clamp-1">{f.notes}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div
                      className="font-display font-bold text-base"
                      style={{ color: overdue ? "#E76F51" : today ? "#E85D04" : "#1F4D3A" }}
                    >
                      {formatDateEU(f.follow_up_date)}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold flex items-center justify-end gap-1">
                      {overdue && (
                        <span className="text-[#E76F51] inline-flex items-center gap-1">
                          <AlertTriangle size={12} /> {Math.abs(diff)}d overdue
                        </span>
                      )}
                      {today && <span className="text-[#E85D04]">Today</span>}
                      {!overdue && !today && diff !== null && (
                        <span className="text-[#6C757D]">in {diff}d</span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
